'use client';

import { useCallback, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { dancerPath } from '@kit/shared/handles';
import { Autocomplete, type AutocompleteOption } from '@kit/ui/autocomplete';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';

import {
  searchDancersByNameAction,
  type DancerNameSearchResult,
} from '../_lib/search-dancers-by-name-action';

const MIN_CHARS = 2;

function displayName(r: DancerNameSearchResult): string {
  if (r.stageName) return r.stageName;
  const full = [r.firstName, r.lastName].filter(Boolean).join(' ');
  return full || r.handle;
}

function initials(label: string): string {
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/**
 * "Find a dancer by name" autocomplete on the public `/dancers` hub
 * (BAL-324). Type a name, pick a result, and land on the dancer's profile
 * (`/<handle>`). Distinct from the grid search/filter below it: this one
 * navigates straight to a profile rather than filtering the catalogue —
 * the flow organisers want for the GDO competition.
 *
 * The `@kit/ui` Autocomplete does its own client-side substring filter on
 * the options it's handed. We pass `serverFiltered` to skip it: the server
 * is the authority on what matched (a stage-name hit needn't substring-match
 * the typed legal name), and the client filter would otherwise hide valid
 * server results mid-keystroke — a flicker-to-empty caused by the async race
 * between the debounced server response and the current input value.
 */
export function DancerNameSearch() {
  const router = useRouter();
  const { t } = useTranslation('dancers');
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [resultsByHandle, setResultsByHandle] = useState<
    Map<string, DancerNameSearchResult>
  >(new Map());
  const [isLoading, startTransition] = useTransition();

  const handleInputChange = useCallback((term: string) => {
    if (term.trim().length < MIN_CHARS) {
      setOptions([]);
      setResultsByHandle(new Map());
      return;
    }

    startTransition(async () => {
      const results = await searchDancersByNameAction({ query: term });

      setResultsByHandle(new Map(results.map((r) => [r.handle, r])));
      setOptions(
        results.map((r) => {
          const label = displayName(r);
          return {
            value: r.handle,
            label,
            // Secondary line: legal name when a stage name is the headline,
            // so two dancers sharing a stage name stay distinguishable.
            description:
              r.stageName &&
              [r.firstName, r.lastName].filter(Boolean).length > 0
                ? [r.firstName, r.lastName].filter(Boolean).join(' ')
                : undefined,
          };
        }),
      );
    });
  }, []);

  const handleSelect = useCallback(
    (value: string | string[] | null) => {
      const handle = Array.isArray(value) ? value[0] : value;
      if (!handle) return;
      router.push(dancerPath(handle));
    },
    [router],
  );

  return (
    <div className="px-4">
      <Autocomplete
        label={t('nameSearch.label')}
        value={undefined}
        onChange={handleSelect}
        options={options}
        loading={isLoading}
        minChars={MIN_CHARS}
        clearOnSelect
        serverFiltered
        onInputChange={handleInputChange}
        placeholder={t('nameSearch.placeholder')}
        emptyMessage={t('nameSearch.empty')}
        loadingMessage={t('nameSearch.loading')}
        inputClassName="max-w-md"
        renderOption={(option) => {
          const r = resultsByHandle.get(option.value);
          const label = option.label;
          return (
            <div className="flex w-full items-center gap-3">
              <Avatar className="h-8 w-8 flex-shrink-0">
                {r?.photoUrl ? <AvatarImage src={r.photoUrl} alt="" /> : null}
                <AvatarFallback className="text-xs">
                  {initials(label)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-foreground truncate font-medium">
                  {label}
                </div>
                {option.description ? (
                  <div className="text-muted-foreground truncate text-sm">
                    {option.description}
                  </div>
                ) : null}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}
